import React, { FC, useMemo } from 'react';

import { Box, HStack, Stack, Text } from '@chakra-ui/react';
import { customTheme } from 'theme';

import { InformationAlert } from 'components/DataDisplay';
import { BorderedBox } from 'components/DataDisplay/Box/BorderedBox';
import { InfoCircleIcon } from 'components/Icons';
import { FormLabel, formLabelStyles, Select } from 'components/index';
import {
  DataPolicyMasks,
  getDefaultMaskOptions,
} from 'containers/Jit/MaskingPolicies/Components/DataPolicyWizard/maskOptions';

import { DataPolicySnowflakeConditions } from './Conditions/DataPolicySnowflakeConditions';

type Props = React.ComponentProps<typeof DataPolicySnowflakeConditions>;

export const DataPolicyAccessRolesCriteria: FC<Props> = props => {
  const { state, updateState } = props;
  // Derived from the same backend metadata the block masks use, minus NO_MASK,
  // so Full Mask stays first (recommended) and ANON/PARTIAL appear when offered.
  const defaultMaskOptions = useMemo(
    () => getDefaultMaskOptions(state?.maskingOptions),
    [state?.maskingOptions],
  );
  const selectedDefaultMask = state?.defaultMask ?? defaultMaskOptions[0];
  const isWeakerThanFullMask =
    selectedDefaultMask?.value &&
    selectedDefaultMask.value !== DataPolicyMasks.FullMask;
  return (
    <BorderedBox p={0} overflow="hidden">
      <Box
        px={6}
        py={4}
        borderBottom="1px solid"
        borderColor="componentBorder"
        color={customTheme.colors.gray['300']}
      >
        <Text>Choose Default Mask</Text>
      </Box>
      <Box px={6} pt={5} pb={8}>
        <Stack spacing={0}>
          <FormLabel
            htmlFor="mask"
            label="Use Default Mask"
            isRequired
            styles={formLabelStyles}
          />
          <HStack>
            <Box w="65%" borderRadius={6} bg="white">
              <Select
                options={defaultMaskOptions}
                value={selectedDefaultMask}
                showClearIndicator={false}
                onChange={val => updateState({ defaultMask: val })}
                showIconInValueContainer={false}
              />
            </Box>
            <Box>for all data groups</Box>
          </HStack>
          {isWeakerThanFullMask && (
            <Box pt={3}>
              <InformationAlert
                icon={<InfoCircleIcon color="orange" />}
                iconSize={5}
                spacing={3}
                description={`Roles not matched below — including new or unlisted roles — will see ${selectedDefaultMask.label} data, not a full mask. Choose Full Mask to keep unknown roles fully masked.`}
              />
            </Box>
          )}
        </Stack>
      </Box>
      <Box borderTop="1px solid" borderColor="componentBorder">
        <Box
          px={6}
          py={3}
          borderBottom="1px solid"
          borderColor="componentBorder"
          color={customTheme.colors.gray['300']}
        >
          <Text>Customize Masking By Choosing Data Groups and Roles</Text>
        </Box>
        <Box px={6} py={6}>
          <DataPolicySnowflakeConditions {...props} />
        </Box>
      </Box>
    </BorderedBox>
  );
};
